import { useState } from "react";
import { Mail, MapPin, Facebook, ExternalLink, CheckCircle, Send, Globe, Linkedin, Instagram } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GOOGLE_MAPS_URL = "https://www.google.com/maps/place/41%C2%B038'16.0%22N+0%C2%B053'53.9%22W/@41.637778,-0.898302,17z";

const Contacto = () => {
  const [formData, setFormData] = useState({
    nombre: "",
    email: "",
    asunto: "",
    mensaje: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      
      if (response.data.success) {
        setSubmitted(true);
        toast.success("Mensaje enviado correctamente", {
          description: "Nos pondremos en contacto contigo pronto."
        });
        setFormData({ nombre: "", email: "", asunto: "", mensaje: "" });
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Error al enviar el mensaje", {
        description: "Por favor, inténtalo de nuevo más tarde."
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="contacto-page">
      {/* Header */}
      <header className="bg-rotary-gradient py-16 md:py-24 px-4">
        <div className="max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 glass-dark px-4 py-2 rounded-full mb-6">
            <Mail className="w-4 h-4 text-[#F7A81B]" />
            <span className="text-white/80 text-sm font-heading uppercase tracking-wider">
              Contacto
            </span>
          </div>
          <h1 className="font-bold-sans text-4xl sm:text-5xl md:text-6xl text-white mb-4 tracking-tight">
            Contáctanos
          </h1>
          <p className="text-white/70 text-base md:text-lg max-w-2xl mx-auto">
            Estamos aquí para responder tus preguntas
          </p>
        </div>
      </header>

      {/* Main Content */}
      <main className="py-12 md:py-20 px-4 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12">
            
            {/* Contact Info */}
            <div data-testid="contact-info">
              <div className="divider-gold mb-6" style={{ marginLeft: 0, marginRight: 'auto' }} />
              <h2 className="font-heading text-2xl md:text-3xl text-slate-800 mb-8">
                Información de Contacto
              </h2>
              
              <div className="space-y-6">
                {/* Web Oficial */}
                <div className="glass-panel p-5 rounded-2xl flex gap-4">
                  <div className="w-12 h-12 bg-[#013E7F]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Globe className="w-5 h-5 text-[#013E7F]" />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-slate-800 mb-1">
                      Web Oficial
                    </h3>
                    <a 
                      href="https://zaragoza.rotary2202.es/es/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#013E7F] hover:underline text-sm flex items-center gap-1"
                      data-testid="contact-web-link"
                    >
                      zaragoza.rotary2202.es
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                </div>

                {/* Address */}
                <div className="glass-panel p-5 rounded-2xl flex gap-4">
                  <div className="w-12 h-12 bg-[#013E7F]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-[#013E7F]" />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-slate-800 mb-1">
                      Dirección
                    </h3>
                    <p className="text-slate-600 text-sm">
                      NH Gran Hotel de Zaragoza
                    </p>
                    <p className="text-slate-600 text-sm">
                      C. Joaquín Costa, 5, Casco Antiguo
                    </p>
                    <p className="text-slate-600 text-sm">
                      50001 Zaragoza
                    </p>
                  </div>
                </div>

                {/* Email */}
                <div className="glass-panel p-5 rounded-2xl flex gap-4">
                  <div className="w-12 h-12 bg-[#013E7F]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-[#013E7F]" />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-slate-800 mb-1">
                      Email
                    </h3>
                    <a 
                      href="mailto:rczaragoza@rotary2202.org"
                      className="text-[#013E7F] hover:underline text-sm"
                      data-testid="contact-email-link"
                    >
                      rczaragoza@rotary2202.org
                    </a>
                  </div>
                </div>

                {/* Redes Sociales */}
                <div className="glass-panel p-5 rounded-2xl">
                  <h3 className="font-heading font-semibold text-slate-800 mb-4">
                    Redes Sociales
                  </h3>
                  <div className="flex flex-wrap gap-3">
                    <a 
                      href="https://www.facebook.com/rotaryclubdezaragoza"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 bg-[#1877F2]/10 text-[#1877F2] px-4 py-2 rounded-xl hover:bg-[#1877F2]/20 transition-colors"
                      data-testid="contact-facebook-link"
                    >
                      <Facebook className="w-5 h-5" />
                      <span className="text-sm font-medium">Facebook</span>
                    </a>
                    <a 
                      href="https://www.linkedin.com/company/rotary-club-zaragoza/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 bg-[#0A66C2]/10 text-[#0A66C2] px-4 py-2 rounded-xl hover:bg-[#0A66C2]/20 transition-colors"
                      data-testid="contact-linkedin-link"
                    >
                      <Linkedin className="w-5 h-5" />
                      <span className="text-sm font-medium">LinkedIn</span>
                    </a>
                    <a 
                      href="https://www.instagram.com/rotary_club_zaragoza/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 bg-[#E4405F]/10 text-[#E4405F] px-4 py-2 rounded-xl hover:bg-[#E4405F]/20 transition-colors"
                      data-testid="contact-instagram-link"
                    >
                      <Instagram className="w-5 h-5" />
                      <span className="text-sm font-medium">Instagram</span>
                    </a>
                  </div>
                </div>
              </div>

              {/* Map Link */}
              <div className="mt-8 glass-panel p-6 rounded-2xl bg-gradient-to-br from-amber-50/50 to-white">
                <h3 className="font-heading font-semibold text-slate-800 mb-3">
                  Ubicación del Reloj Solar
                </h3>
                <p className="text-slate-600 text-sm mb-4">
                  Junto a la Cámara de Comercio de Zaragoza
                </p>
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    window.open(GOOGLE_MAPS_URL, '_blank');
                  }}
                  className="inline-flex items-center gap-2 text-[#013E7F] hover:text-[#002D5C] font-medium text-sm cursor-pointer"
                  data-testid="contact-maps-link"
                >
                  <MapPin className="w-4 h-4" />
                  Ver en Google Maps
                  <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </div>

            {/* Contact Form */}
            <div data-testid="contact-form-section">
              <div className="divider-gold mb-6" style={{ marginLeft: 0, marginRight: 'auto' }} />
              <h2 className="font-heading text-2xl md:text-3xl text-slate-800 mb-8">
                Envíanos un Mensaje
              </h2>

              {submitted ? (
                <div className="glass-panel bg-gradient-to-br from-green-50 to-white border-green-200 rounded-2xl p-8 text-center">
                  <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <h3 className="font-heading text-xl text-slate-800 mb-2">
                    ¡Mensaje Enviado!
                  </h3>
                  <p className="text-slate-600 mb-6">
                    Gracias por contactarnos. Nos pondremos en contacto contigo pronto.
                  </p>
                  <Button
                    onClick={() => setSubmitted(false)}
                    variant="outline"
                    className="border-[#013E7F] text-[#013E7F] rounded-xl"
                    data-testid="send-another-btn"
                  >
                    Enviar otro mensaje
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="glass-panel p-6 md:p-8 rounded-2xl space-y-6" data-testid="contact-form">
                  <div>
                    <Label htmlFor="nombre" className="text-slate-700 font-heading">
                      Nombre *
                    </Label>
                    <Input
                      id="nombre"
                      name="nombre"
                      type="text"
                      value={formData.nombre}
                      onChange={handleChange}
                      required
                      className="mt-2 form-input rounded-xl"
                      placeholder="Tu nombre"
                      data-testid="input-nombre"
                    />
                  </div>

                  <div>
                    <Label htmlFor="email" className="text-slate-700 font-heading">
                      Email *
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="mt-2 form-input rounded-xl"
                      placeholder="tu@email.com"
                      data-testid="input-email"
                    />
                  </div>

                  <div>
                    <Label htmlFor="asunto" className="text-slate-700 font-heading">
                      Asunto *
                    </Label>
                    <Input
                      id="asunto"
                      name="asunto"
                      type="text"
                      value={formData.asunto}
                      onChange={handleChange}
                      required
                      className="mt-2 form-input rounded-xl"
                      placeholder="Asunto del mensaje"
                      data-testid="input-asunto"
                    />
                  </div>

                  <div>
                    <Label htmlFor="mensaje" className="text-slate-700 font-heading">
                      Mensaje *
                    </Label>
                    <Textarea
                      id="mensaje"
                      name="mensaje"
                      value={formData.mensaje}
                      onChange={handleChange}
                      required
                      className="mt-2 form-input min-h-[150px] rounded-xl"
                      placeholder="Escribe tu mensaje aquí..."
                      data-testid="input-mensaje"
                    />
                  </div>

                  <Button
                    type="submit"
                    className="btn-primary w-full rounded-xl py-6"
                    disabled={isSubmitting}
                    data-testid="submit-contact-btn"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                        Enviando...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Enviar Mensaje
                      </>
                    )}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Rotary Info */}
      <section className="py-12 px-4 bg-gradient-to-br from-amber-50/50 to-orange-50/30">
        <div className="max-w-3xl mx-auto text-center glass-panel p-8 rounded-3xl">
          <h3 className="font-heading text-xl text-slate-800 mb-4">
            Rotary Club de Zaragoza
          </h3>
          <p className="text-slate-600 mb-6">
            Desde 1925, comprometidos con el servicio a la comunidad y los valores rotarios.
          </p>
          <p className="font-accent text-[#013E7F] italic text-lg">
            "Dar de sí antes de pensar en sí"
          </p>
        </div>
      </section>
    </div>
  );
};

export default Contacto;
